/**
 * Staff in-app notification endpoints.
 */
import { apiRequest, normalizeList } from "./staffUtils";
import { toDisplayString } from "../../utils/toDisplayString";

export const staffNotificationInitialState = {
  staffNotifications: [],
  staffNotificationsLoading: false,
  staffNotificationsError: null,
  staffNotificationsTotal: 0,
  staffNotificationsPages: 1,
  staffNotificationDetail: null,
  staffNotificationDetailLoading: false,
  staffNotificationDetailError: null,
  staffNotificationUnreadCount: 0,
  staffNotificationUnreadCountLoading: false,
  staffNotificationUnreadCountError: null,
  staffNotificationMutationLoading: false,
  staffNotificationMutationError: null,
};

export function createStaffNotificationActions(set, get) {
  async function mutate(path) {
    set({ staffNotificationMutationLoading: true, staffNotificationMutationError: null });
    try {
      // Both read endpoints take no request body.
      await apiRequest(get, path, { method: "PATCH" });
      set({ staffNotificationMutationLoading: false, staffNotificationMutationError: null });
      return { success: true };
    } catch (err) {
      const error = toDisplayString(err.message, "Notification action failed");
      set({ staffNotificationMutationLoading: false, staffNotificationMutationError: error });
      return { success: false, error };
    }
  }

  return {
    fetchStaffNotifications: async (params = {}) => {
      set({ staffNotificationsLoading: true, staffNotificationsError: null });
      try {
        const query = new URLSearchParams(params).toString();
        const data = await apiRequest(
          get,
          `/api/staff/notifications${query ? `?${query}` : ""}`
        );
        const { items, total, totalPages } = normalizeList(data, ["notifications"]);
        set({
          staffNotificationsLoading: false,
          staffNotifications: items,
          staffNotificationsTotal: total,
          staffNotificationsPages: totalPages,
        });
        return { success: true, items, total, totalPages };
      } catch (err) {
        const error = toDisplayString(err.message, "Failed to load notifications");
        set({ staffNotificationsLoading: false, staffNotificationsError: error });
        return { success: false, error };
      }
    },
    fetchStaffNotification: async (id) => {
      set({ staffNotificationDetailLoading: true, staffNotificationDetailError: null });
      try {
        const data = await apiRequest(get, `/api/staff/notifications/${id}`);
        const notification = data?.data ?? data?.notification ?? data;
        set({ staffNotificationDetailLoading: false, staffNotificationDetail: notification });
        return { success: true, data: notification };
      } catch (err) {
        const error = toDisplayString(err.message, "Failed to load notification");
        set({ staffNotificationDetailLoading: false, staffNotificationDetailError: error });
        return { success: false, error };
      }
    },
    fetchStaffNotificationUnreadCount: async () => {
      set({
        staffNotificationUnreadCountLoading: true,
        staffNotificationUnreadCountError: null,
      });
      try {
        const data = await apiRequest(get, "/api/staff/notifications/unread-count");
        const count = Number(data?.count ?? data?.unreadCount ?? data?.data?.count ?? 0) || 0;
        set({
          staffNotificationUnreadCountLoading: false,
          staffNotificationUnreadCount: count,
        });
        return { success: true, count };
      } catch (err) {
        const error = toDisplayString(err.message, "Failed to load unread count");
        set({
          staffNotificationUnreadCountLoading: false,
          staffNotificationUnreadCountError: error,
        });
        return { success: false, error };
      }
    },
    markStaffNotificationRead: async (id) => {
      const result = await mutate(`/api/staff/notifications/${id}/read`);
      if (result.success) {
        set((state) => ({
          staffNotifications: state.staffNotifications.map((item) =>
            item.id === id ? { ...item, read: true, status: "read" } : item
          ),
          staffNotificationUnreadCount: Math.max(0, state.staffNotificationUnreadCount - 1),
        }));
      }
      return result;
    },
    markAllStaffNotificationsRead: async () => {
      const result = await mutate("/api/staff/notifications/read-all");
      if (result.success) {
        set((state) => ({
          staffNotifications: state.staffNotifications.map((item) => ({
            ...item,
            read: true,
            status: "read",
          })),
          staffNotificationUnreadCount: 0,
        }));
      }
      return result;
    },
  };
}
